/**
 * Newsletter API wrappers. All endpoints go through `authApi` which targets
 * the auth worker (the same backend used for `/api/v1/admin/referral/*`).
 */

import { authApi } from './api';
import type {
  AudienceCounts,
  NewsletterAudience,
  NewsletterCampaign,
  NewsletterListResponse,
  NewsletterSendLogResponse,
  NewsletterSingleResponse,
} from './newsletter-types';

export interface CreateCampaignInput {
  subject: string;
  mjml: string;
  html: string;
  audience: NewsletterAudience;
  from_name?: string;
  from_email?: string;
  reply_to?: string | null;
}

export interface UpdateCampaignInput {
  subject?: string;
  mjml?: string;
  html?: string;
  audience?: NewsletterAudience;
  from_name?: string;
  from_email?: string;
  reply_to?: string | null;
}

export async function getAudienceCounts(): Promise<AudienceCounts> {
  const res = await authApi.get<{ ok: boolean } & AudienceCounts>(
    '/api/v1/admin/newsletter/audience',
  );
  return {
    free: res.free,
    paid: res.paid,
    both: res.both,
  };
}

export async function listCampaigns(params: {
  status?: string;
  limit?: number;
  offset?: number;
} = {}): Promise<NewsletterListResponse> {
  return authApi.get<NewsletterListResponse>('/api/v1/admin/newsletter/campaigns', {
    params: {
      status: params.status,
      limit: params.limit ?? 50,
      offset: params.offset ?? 0,
    },
  });
}

export async function getCampaign(id: string): Promise<NewsletterCampaign> {
  const res = await authApi.get<NewsletterSingleResponse>(
    `/api/v1/admin/newsletter/campaigns/${id}`,
  );
  return res.campaign;
}

export async function createCampaign(input: CreateCampaignInput): Promise<NewsletterCampaign> {
  const res = await authApi.post<NewsletterSingleResponse>(
    '/api/v1/admin/newsletter/campaigns',
    input,
  );
  return res.campaign;
}

export async function updateCampaign(
  id: string,
  input: UpdateCampaignInput,
): Promise<NewsletterCampaign> {
  const res = await authApi.patch<NewsletterSingleResponse>(
    `/api/v1/admin/newsletter/campaigns/${id}`,
    input,
  );
  return res.campaign;
}

export async function deleteCampaign(id: string): Promise<void> {
  await authApi.delete<{ ok: boolean }>(`/api/v1/admin/newsletter/campaigns/${id}`);
}

export async function sendTest(id: string): Promise<{ ok: boolean; recipient?: string; error?: string }> {
  return authApi.post<{ ok: boolean; recipient?: string; error?: string }>(
    `/api/v1/admin/newsletter/campaigns/${id}/test`,
    {},
  );
}

export interface ScheduleInput {
  scheduled_for: number;
  timezone?: string;
}

export async function scheduleCampaign(
  id: string,
  input: ScheduleInput,
): Promise<NewsletterCampaign> {
  const res = await authApi.post<NewsletterSingleResponse>(
    `/api/v1/admin/newsletter/campaigns/${id}/schedule`,
    input,
  );
  return res.campaign;
}

export async function cancelCampaign(id: string): Promise<NewsletterCampaign> {
  const res = await authApi.post<NewsletterSingleResponse>(
    `/api/v1/admin/newsletter/campaigns/${id}/cancel`,
    {},
  );
  return res.campaign;
}

export async function sendNow(id: string) {
  return authApi.post<{
    ok: boolean;
    result: {
      ok: boolean;
      status: 'sent' | 'failed';
      total_recipients: number;
      sent_count: number;
      failed_count: number;
      error?: string;
    };
  }>(`/api/v1/admin/newsletter/campaigns/${id}/send-now`, {});
}

export async function listSends(
  id: string,
  params: { status?: string; limit?: number; offset?: number } = {},
): Promise<NewsletterSendLogResponse> {
  return authApi.get<NewsletterSendLogResponse>(
    `/api/v1/admin/newsletter/campaigns/${id}/sends`,
    { params: { ...params } },
  );
}

/**
 * Render MJML to HTML client-side. Returns the rendered HTML string.
 * Uses the `mjml-browser` package (a worker-safe port of MJML).
 *
 * Throws if MJML is invalid; callers should display the error inline.
 */
export async function renderMjmlToHtml(mjml: string): Promise<string> {
  const mod = (await import('mjml-browser')) as unknown as {
    default: (input: string, opts?: Record<string, unknown>) => {
      html: string;
      errors?: Array<{ message?: string; line?: number }>;
    };
  };
  const result = mod.default(mjml, { validationLevel: 'soft' });
  if (result.errors && result.errors.length > 0) {
    const first = result.errors[0];
    throw new Error(`MJML: ${first?.message ?? 'invalid'}`);
  }
  return result.html;
}

/**
 * Generate a starter MJML template that's safe to render — used by the
 * builder's "New Campaign" page so a fresh draft opens with sensible content.
 */
export const STARTER_MJML = `<mjml>
  <mj-head>
    <mj-title>Voice Science Newsletter</mj-title>
    <mj-preview>Voice science in your inbox.</mj-preview>
    <mj-attributes>
      <mj-all font-family="Helvetica, Arial, sans-serif" />
      <mj-text font-size="15px" line-height="1.6" color="#1e2125" />
      <mj-button background-color="#65A0A8" color="#ffffff" font-weight="600" border-radius="6px" />
    </mj-attributes>
  </mj-head>
  <mj-body background-color="#f5f5f5">
    <mj-section background-color="#ffffff" padding="32px 32px 24px">
      <mj-column>
        <mj-image
          src="https://www.voicescience.org/assets/images/logo-100.png"
          alt="Voice Science"
          width="80"
          align="center"
        />
        <mj-text align="center" font-size="22px" font-weight="700" padding-top="20px">
          Subject line here
        </mj-text>
      </mj-column>
    </mj-section>

    <mj-section background-color="#ffffff" padding="8px 32px 32px">
      <mj-column>
        <mj-text>
          Hi there,
        </mj-text>
        <mj-text>
          Replace this with your introduction. Keep it concise and conversational — the goal is to
          earn the next paragraph.
        </mj-text>
        <mj-button href="https://www.voicescience.org" padding-top="12px">
          Read more
        </mj-button>
      </mj-column>
    </mj-section>

    <mj-section background-color="#f5f5f5" padding="16px 32px">
      <mj-column>
        <mj-text align="center" font-size="11px" color="#616161">
          You are receiving this because you subscribed at voicescience.org.
        </mj-text>
        <mj-text align="center" font-size="11px" color="#616161">
          <a href="mailto:unsubscribe@mail.voicescience.org?subject=unsubscribe" style="color:#65A0A8;">Unsubscribe</a>
        </mj-text>
      </mj-column>
    </mj-section>
  </mj-body>
</mjml>`;
